// Client wrapper for the premium TTS proxy (Voice Tutor §11.3, Slice C).
import { httpsCallable } from 'firebase/functions';

let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

export interface SynthesizedSpeech {
  audioContent: string; // base64
  mimeType: string; // e.g. audio/mpeg
}

/**
 * Synthesise premium speech server-side. Throws if the user isn't entitled or
 * premium voice isn't configured — callers should fall back to browser TTS.
 */
export async function synthesizeSpeech(
  text: string,
  opts?: { voiceName?: string; speakingRate?: number },
): Promise<SynthesizedSpeech> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<
    { text: string; voiceName?: string; speakingRate?: number },
    SynthesizedSpeech
  >(functions, 'synthesizeSpeech');
  const result = await fn({ text, ...opts });
  return result.data;
}

/**
 * Transcribe recorded speech (base64 audio) to text via the server-side Whisper
 * proxy. Throws if the user isn't entitled (Pro/Teams) or voice isn't configured.
 */
export async function transcribeSpeech(input: {
  audioBase64: string;
  mimeType?: string;
  fileName?: string;
}): Promise<{ text: string }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<typeof input, { text: string }>(functions, 'transcribeSpeech');
  const result = await fn(input);
  return result.data;
}
